import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';

const WebsiteDevelopment: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Website Development');

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <div
        className="relative py-20"
        style={{
          backgroundImage: `url('/hero-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto px-8">
          <button
            onClick={() => navigate('/')}
            className="mb-6 text-white hover:text-orange-400 flex items-center transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Terug naar Home
          </button>
          <h1 className="text-5xl font-bold text-white mb-4">Website Development</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Professionele websites die je bedrijf online laten groeien
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-8 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-blue-900 mb-6">Uw online visitekaartje</h2>
            <p className="text-lg text-gray-700 mb-4">
              Een professionele website is essentieel voor elk modern bedrijf. Bij SnapLogic ontwikkelen we
              websites die niet alleen mooi zijn, maar ook resultaten opleveren. Of je nu een eenvoudige
              bedrijfswebsite nodig hebt of een complexe webapplicatie, wij maken het mogelijk.
            </p>
          </section>

          {/* What We Offer */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-blue-900 mb-6">Wat wij bieden</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-blue-800 mb-3">💻 Responsive Design</h3>
                <p className="text-gray-700">
                  Jouw website ziet er perfect uit op elk apparaat - desktop, tablet en mobiel.
                  We zorgen voor een naadloze gebruikerservaring op alle schermformaten.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-blue-800 mb-3">⚡ Snelle Laadtijden</h3>
                <p className="text-gray-700">
                  Performance is cruciaal. Onze websites laden razendsnel, wat zorgt voor betere
                  conversies en een hogere positie in Google.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-blue-800 mb-3">🔒 Veilig & Betrouwbaar</h3>
                <p className="text-gray-700">
                  SSL-certificaten, regelmatige updates en veilige code standaarden zorgen ervoor
                  dat jouw website en data goed beschermd zijn.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-blue-800 mb-3">📊 Analytics & Inzichten</h3>
                <p className="text-gray-700">
                  We integreren analytics tools zodat je altijd inzicht hebt in bezoekersgedrag
                  en de prestaties van je website kunt monitoren.
                </p>
              </div>
            </div>
          </section>

          {/* Process */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-blue-900 mb-6">Ons proces</h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Intake & Planning</h3>
                  <p className="text-gray-700">
                    We beginnen met een uitgebreid gesprek om jouw wensen, doelen en doelgroep
                    in kaart te brengen.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Design & Prototype</h3>
                  <p className="text-gray-700">
                    Indien je nog geen bestaand design hebt, maken we eerst een prototype design.
                    Dit laten we aan jou zien zodat je precies weet hoe je website eruit komt te zien
                    voordat we beginnen met bouwen.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Development</h3>
                  <p className="text-gray-700">
                    Onze developers bouwen je website met de nieuwste technologieën en best practices.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Testing & Launch</h3>
                  <p className="text-gray-700">
                    Uitgebreid testen op alle apparaten en browsers, gevolgd door een soepele lancering.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="bg-blue-900 text-white p-8 rounded-xl text-center">
            <h2 className="text-3xl font-bold mb-4">Klaar om te starten?</h2>
            <p className="text-xl mb-6">
              Laat ons jouw idee werkelijkheid maken met een professionele website
            </p>
            <button
              onClick={() => navigate('/?step=1')}
              className="px-8 py-3 rounded-lg font-bold transition transform hover:scale-105"
              style={{
                background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
              }}
            >
              Start je project
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WebsiteDevelopment;
